<template>
  <div class="auth">
    <!-- Left hero -->
    <div class="auth__hero">
      <div class="auth__hero-overlay"></div>
      <div class="auth__hero-content">
        <div class="brand">
          <img v-if="!brandImgFailedDark" class="brand__img" src="/brand/logo-dark.png" alt="KissMora" @error="brandImgFailedDark = true">
          <template v-else>
            <svg class="brand__icon" viewBox="0 0 40 40" fill="none">
              <path d="M20 34s-12-7.4-12-16a7 7 0 0 1 12-4.9A7 7 0 0 1 32 18c0 8.6-12 16-12 16z" fill="#fff"/>
            </svg>
            <span class="brand__name">KissMora</span>
          </template>
        </div>
        <div class="hero-quote">
          <h2>Your story<br/>starts tonight. 💋</h2>
          <p>Find your spark. Join 250,000+ singles finding something real on KissMora.</p>
        </div>
        <ul class="benefits">
          <li><span>✨</span> Verified profiles only</li>
          <li><span>🔒</span> Your number stays private</li>
          <li><span>🎁</span> Free to join, always</li>
        </ul>
      </div>
    </div>

    <!-- Right form -->
    <div class="auth__form-wrap">
      <div class="auth__form">
        <div class="auth__top">
          <div class="brand brand--mobile">
            <img v-if="!brandImgFailedLight" class="brand__img" src="/brand/logo-light.png" alt="KissMora" @error="brandImgFailedLight = true">
            <template v-else>
              <svg class="brand__icon" viewBox="0 0 40 40" fill="none">
                <path d="M20 34s-12-7.4-12-16a7 7 0 0 1 12-4.9A7 7 0 0 1 32 18c0 8.6-12 16-12 16z" fill="url(#rkg)"/>
                <defs>
                  <linearGradient id="rkg" x1="8" y1="8" x2="32" y2="34"><stop stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/></linearGradient>
                </defs>
              </svg>
              <span class="brand__name">KissMora</span>
            </template>
          </div>
          <button class="backbtn" @click="goBack" aria-label="Back">←</button>
        </div>

        <!-- STEP DOTS -->
        <div class="steps">
          <div class="dot" :class="{active: step>=1, done: step>1}">1</div>
          <div class="bar" :class="{filled: step>1}"></div>
          <div class="dot" :class="{active: step>=2, done: step>2}">2</div>
          <div class="bar" :class="{filled: step>2}"></div>
          <div class="dot" :class="{active: step>=3}">3</div>
        </div>

        <!-- STEP 1 — Phone -->
        <div v-if="step===1">
          <h1 class="auth__title">Let's start with your phone.</h1>
          <p class="auth__sub">We'll send a 6-digit code to verify it's really you.</p>

          <form class="form" @submit.prevent="sendOtp()">
            <label class="field">
              <span class="field__label">Phone number</span>
              <div class="field__box phone-box">
                <button type="button" class="dial" @click="pickerOpen=true">
                  <span class="flag">{{ country.flag }}</span>
                  <span class="code">+{{ country.dial }}</span>
                  <span class="chev">▾</span>
                </button>
                <input v-model="phone" type="tel" inputmode="tel" placeholder="Phone number"/>
              </div>
            </label>
            <p class="hint">Standard message rates may apply. We never share your number.</p>
            <button type="submit" class="btn btn--primary" :disabled="loading">
              <span v-if="!loading">Send verification code</span><span v-else class="spinner"></span>
            </button>
          </form>
        </div>

        <!-- STEP 2 — OTP -->
        <div v-else-if="step===2">
          <h1 class="auth__title">Enter your code.</h1>
          <p class="auth__sub">
            We sent a 6-digit code to <strong>+{{ country.dial }} {{ phone }}</strong>.
            <a href="#" class="link" @click.prevent="step=1; resetOtp(); devCode=''">Change number</a>
          </p>

          <div v-if="devCode" class="dev-banner">
            Verification code: <strong>{{ devCode }}</strong>
            <span v-if="!smsDelivered"> (enter below — SMS not delivered)</span>
          </div>

          <form class="form" @submit.prevent="verifyOtp">
            <label class="field">
              <span class="field__label">6-digit verification code</span>
              <div class="auth-otp" role="group" aria-label="Verification code">
                <input v-for="(_,i) in 6" :key="'otp-'+i"
                       :ref="'otpBox'+i"
                       v-model="otp[i]"
                       type="tel"
                       inputmode="numeric"
                       pattern="[0-9]*"
                       maxlength="1"
                       autocomplete="one-time-code"
                       class="auth-otp__cell"
                       :aria-label="'Digit ' + (i + 1)"
                       @input="onOtpInput(i, $event)"
                       @keydown="onOtpKey(i, $event)"
                       @paste="onOtpPaste"
                       @focus="$event.target.select()"/>
              </div>
            </label>
            <div class="resend">
              <span v-if="cooldown>0">Resend in {{cooldown}}s</span>
              <a v-else href="#" class="link" @click.prevent="sendOtp(true)">Resend code</a>
            </div>
            <button type="submit" class="btn btn--primary" :disabled="loading || otpString.length!==6">
              <span v-if="!loading">Verify code</span><span v-else class="spinner"></span>
            </button>
          </form>
        </div>

        <!-- STEP 3 — Profile -->
        <div v-else>
          <h1 class="auth__title">Create your profile.</h1>
          <p class="auth__sub">Almost there. Let's set up who you are on KissMora.</p>

          <form class="form" @submit.prevent="doRegister">
            <label class="field">
              <span class="field__label">Username</span>
              <div class="field__box">
                <span class="field__ico">👤</span>
                <input v-model="username" type="text" placeholder="Choose a username" autocomplete="username"/>
              </div>
            </label>

            <label class="field">
              <span class="field__label">Email <span class="field__opt">(optional — you can verify later)</span></span>
              <div class="field__box">
                <span class="field__ico">✉️</span>
                <input v-model="email" type="email" placeholder="you@example.com" autocomplete="email"/>
              </div>
            </label>

            <label class="field">
              <span class="field__label">Password</span>
              <div class="field__box">
                <span class="field__ico">🔒</span>
                <input v-model="password" :type="showPass?'text':'password'" placeholder="Min 6 characters" autocomplete="new-password"/>
                <button type="button" class="eye" @click="showPass=!showPass" aria-label="Toggle">{{ showPass?'🙈':'👁️' }}</button>
              </div>
            </label>

            <label class="field">
              <span class="field__label">Invitation code</span>
              <div class="field__box">
                <span class="field__ico">💌</span>
                <input v-model="inviteCode" type="text" placeholder="Enter invite code"/>
              </div>
            </label>

            <label class="check check--terms">
              <input type="checkbox" v-model="agree"/>
              <span>I'm 18+ and agree to the <a href="#" class="link">Terms</a> &amp; <a href="#" class="link">Privacy Policy</a>.</span>
            </label>

            <button type="submit" class="btn btn--primary" :disabled="loading">
              <span v-if="!loading">Create my account 💕</span><span v-else class="spinner"></span>
            </button>
          </form>
        </div>

        <p class="auth__foot">
          Already have an account? <router-link to="/Login">Sign in</router-link>
        </p>
      </div>
    </div>

    <!-- COUNTRY PICKER -->
    <div v-if="pickerOpen" class="picker-mask" @click.self="pickerOpen=false">
      <div class="picker">
        <div class="picker__head">
          <h3>Select your country</h3>
          <button class="picker__x" @click="pickerOpen=false">✕</button>
        </div>
        <input class="picker__search" v-model="pickerSearch" placeholder="Search country..."/>
        <div class="picker__list">
          <div v-for="c in filteredCountries" :key="c.iso"
               class="picker__item"
               :class="{active: c.iso===country.iso}"
               @click="selectCountry(c)">
            <span class="picker__flag">{{ c.flag }}</span>
            <span class="picker__name">{{ c.name }}</span>
            <span class="picker__dial">+{{ c.dial }}</span>
          </div>
          <div v-if="filteredCountries.length===0" class="picker__empty">No match</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { countries, findCountry } from '../../assets/countries.js'

export default {
  name: 'AuthRegister',
  data(){
    return {
      step: 1,
      brandImgFailedDark: false,
      brandImgFailedLight: false,
      country: findCountry('US'),
      phone: '',
      otp: ['','','','','',''],
      devCode: '',
      smsDelivered: false,
      otpVerified: false,
      cooldown: 0,
      cooldownTimer: null,

      username: '',
      email: '',
      password: '',
      inviteCode: '',
      showPass: false,
      agree: true,

      loading: false,
      pickerOpen: false,
      pickerSearch: '',
      countries
    }
  },
  computed:{
    otpString(){ return this.otp.join(''); },
    filteredCountries(){
      const q = this.pickerSearch.trim().toLowerCase();
      if(!q) return this.countries;
      return this.countries.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.dial.startsWith(q.replace('+','')) ||
        c.iso.toLowerCase().includes(q)
      );
    }
  },
  created(){
    if(localStorage.getItem('token')) this.$router.replace('/Home');
    this.detectCountry();
    // Pre-fill invite code from ?code= query (referral links from admin panel).
    var q = this.$route && this.$route.query;
    if (q) {
      var c = q.code || q.ref || q.invite || '';
      if (c) {
        this.inviteCode = String(c).trim().toUpperCase();
        try { localStorage.setItem('km_referral_code', this.inviteCode); } catch(e){ /* storage may be disabled */ }
      } else {
        try {
          var stored = localStorage.getItem('km_referral_code');
          if (stored) this.inviteCode = stored;
        } catch(e){ /* storage may be disabled */ }
      }
    }
  },
  beforeDestroy(){ if(this.cooldownTimer) clearInterval(this.cooldownTimer); },
  methods:{
    goBack(){
      if(this.step === 3){ this.step = 2; return; }
      if(this.step === 2){ this.step = 1; this.otpVerified = false; return; }
      this.$router.push('/');
    },
    focusOtpCell(index){
      this.$nextTick(()=>{
        const ref = this.$refs['otpBox' + index];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if(el && el.focus) el.focus();
      });
    },
    resetOtp(){
      this.otp = ['','','','','',''];
      this.otpVerified = false;
    },
    selectCountry(c){ this.country = c; this.pickerOpen = false; this.pickerSearch = ''; },
    detectCountry(){
      const saved = localStorage.getItem('detected_country');
      if(saved){ const c = findCountry(saved); if(c) this.country = c; return; }
      try {
        fetch('https://ipapi.co/json/').then(r=>r.json()).then(d=>{
          if(d && d.country_code){
            localStorage.setItem('detected_country', d.country_code);
            const c = findCountry(d.country_code); if(c) this.country = c;
          }
        }).catch(()=>{});
      } catch(e){ /* ignore */ }
    },
    startCooldown(){
      // KissMora: cooldown removed — users can resend immediately.
      this.cooldown = 0;
      if(this.cooldownTimer){ clearInterval(this.cooldownTimer); this.cooldownTimer = null; }
    },
    sendOtp(isResend){
      const p = String(this.phone).trim();
      if(!p) return this.$toast('Please enter your phone number');
      if(p.length < 5) return this.$toast('That phone number looks too short');
      if(isResend) this.resetOtp();
      this.loading = true;
      this.$http({
        url:'send_otp', method:'post',
        data:{ phone:p, country_code:this.country.dial, country_iso:this.country.iso }
      }).then(res=>{
        this.loading = false;
        if(res && res.code === 200){
          var dev = (res.data && res.data.dev_code) ? String(res.data.dev_code) : '';
          var delivered = !!(res.data && res.data.delivered);
          var providerErr = (res.data && res.data.provider_error) ? String(res.data.provider_error) : '';
          this.devCode = dev;
          this.smsDelivered = delivered;
          if (delivered) {
            this.$toast.success(res.msg || 'Code sent to your phone');
          } else if (dev && providerErr) {
            this.$toast({ message: providerErr + ' Use the code shown below.', duration: 5000 });
          } else if (dev) {
            this.$toast('Enter the verification code shown below');
          } else {
            this.$toast(res.msg || 'Code sent');
          }
          if (dev && /^\d{6}$/.test(dev)) {
            for (var i = 0; i < 6; i++) this.$set(this.otp, i, dev[i]);
          } else {
            this.resetOtp();
          }
          this.startCooldown();
          if(!isResend){
            this.step = 2;
            this.otpVerified = false;
            this.$nextTick(()=>{
              var target = 0;
              for (var j = 0; j < 6; j++) { if (!this.otp[j]) { target = j; break; } target = 5; }
              this.focusOtpCell(target);
              var el = this.$el.querySelector('.auth-otp');
              if(el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          }
        } else {
          this.$toast((res && res.msg) || 'Could not send code');
        }
      }).catch(()=>{ this.loading = false; this.$toast('Network error'); });
    },
    onOtpInput(i, e){
      var raw = (e.target.value || '').replace(/\D/g,'');
      if(raw.length > 1){
        for(var k=0;k<6;k++) this.$set(this.otp, k, raw[k] || '');
        this.focusOtpCell(Math.min(raw.length, 5));
        return;
      }
      const v = raw.slice(-1);
      this.$set(this.otp, i, v);
      if(v && i < 5) this.focusOtpCell(i + 1);
    },
    onOtpKey(i, e){
      if(e.key === 'Backspace' && !this.otp[i] && i > 0){
        this.focusOtpCell(i - 1);
      }
    },
    onOtpPaste(e){
      const paste = (e.clipboardData.getData('text')||'').replace(/\D/g,'').slice(0,6);
      if(paste.length){
        e.preventDefault();
        for(let i=0;i<6;i++) this.$set(this.otp, i, paste[i] || '');
        this.focusOtpCell(Math.min(paste.length, 5) || 0);
      }
    },
    verifyOtp(){
      if(this.otpString.length !== 6) return this.$toast('Enter all 6 digits');
      this.loading = true;
      this.$http({
        url:'verify_otp', method:'post',
        data:{ phone:String(this.phone).trim(), country_code:this.country.dial, code:this.otpString }
      }).then(res=>{
        this.loading = false;
        if(res && res.code === 200){
          this.otpVerified = true;
          this.$toast.success('Phone verified!');
          this.step = 3;
        } else {
          this.$toast((res && res.msg) || 'Invalid or expired code');
        }
      }).catch(()=>{ this.loading = false; this.$toast('Network error'); });
    },
    doRegister(){
      if(!this.otpVerified || this.step !== 3){
        this.$toast('Please verify your phone number first');
        this.step = this.otpVerified ? 3 : 2;
        return;
      }
      if(!this.username) return this.$toast('Please choose a username');
      if(!this.password || this.password.length < 6) return this.$toast('Password must be at least 6 characters');
      if(!this.inviteCode) return this.$toast('Please enter your invitation code');
      if(!this.agree) return this.$toast('Please accept the terms to continue');
      var em = (this.email || '').trim();
      if(em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)){
        return this.$toast('That email address doesn\'t look right');
      }
      this.loading = true;
      this.$http({
        url:'member_register', method:'post',
        data:{
          username: this.username.trim(),
          email: em,
          password: this.password,
          code: this.inviteCode.trim(),
          phone: String(this.phone).trim(),
          country_code: this.country.dial,
          country_iso: this.country.iso,
          otp: this.otpString,
          lang: localStorage.getItem('lang') || 'en'
        }
      }).then(res=>{
        this.loading = false;
        if(res && res.code === 200){
          this.$toast.success('Welcome to KissMora 💕');
          localStorage.setItem('token', res.data);
          this.$router.push('/Home');
        } else {
          this.$toast((res && res.msg) || 'Could not create account');
        }
      }).catch(()=>{ this.loading = false; this.$toast('Network error'); });
    }
  }
}
</script>

<style scoped>
.auth {
  min-height: 100vh;
  display: grid; grid-template-columns: 1fr 1fr;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  background: #fff9fb;
}
.auth * { box-sizing: border-box; }

.auth__hero {
  position: relative; overflow: hidden;
  background: #000 url('/brand/poster.png') center / cover no-repeat;
  min-height: 100vh;
}
.auth__hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,.28) 0%, rgba(0,0,0,.62) 100%);
}
.auth__hero-content {
  position: relative; z-index: 1; padding: 48px; color: #fff;
  display: flex; flex-direction: column; justify-content: space-between; min-height: 100vh;
}
.brand { display: flex; align-items: center; gap: 10px; }
.brand__img { height: 76px; max-width: 320px; object-fit: contain; display: block; }
.brand--mobile .brand__img { height: 56px; max-width: 240px; }
.brand__icon { width: 36px; height: 36px; }
.brand__name { font-size: 24px; font-weight: 800; letter-spacing: -.02em; }
.hero-quote h2 { font-size: 52px; line-height: 1.05; font-weight: 800; margin: 0 0 16px; letter-spacing: -.02em; }
.hero-quote p { font-size: 18px; opacity: .9; max-width: 420px; }
.benefits { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 14px; font-size: 16px; }
.benefits li { display: flex; align-items: center; gap: 12px; }
.benefits span { font-size: 22px; }

.auth__form-wrap { display: flex; align-items: center; justify-content: center; padding: 48px 24px; }
.auth__form { width: 100%; max-width: 440px; }
.auth__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; }
.brand--mobile { display: none; }
.backbtn {
  background: rgba(26,16,51,.06); border: none; width: 40px; height: 40px; border-radius: 50%;
  font-size: 20px; cursor: pointer; color: #1a1033;
}
.backbtn:hover { background: rgba(26,16,51,.12); }

/* Steps */
.steps { display: flex; align-items: center; gap: 8px; margin-bottom: 32px; }
.dot {
  width: 32px; height: 32px; border-radius: 50%; background: #eee2ec;
  color: #b8a9c9; font-weight: 700; font-size: 14px;
  display: flex; align-items: center; justify-content: center;
  transition: all .25s;
}
.dot.active { background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff; transform: scale(1.08); box-shadow: 0 6px 16px rgba(255,77,141,.35); }
.dot.done { background: #a020f0; color: #fff; }
.bar { flex: 1; height: 3px; background: #eee2ec; border-radius: 2px; transition: background .25s; }
.bar.filled { background: linear-gradient(90deg, #ff4d8d, #a020f0); }

.auth__title { font-size: 30px; font-weight: 800; margin: 0 0 8px; color: #1a1033; letter-spacing: -.02em; }
.auth__sub { color: #6b5c85; font-size: 15px; margin: 0 0 28px; line-height: 1.5; }
.auth__sub strong { color: #1a1033; }

.form { display: flex; flex-direction: column; gap: 18px; }
.field { display: flex; flex-direction: column; gap: 8px; }
.field__label { font-size: 13px; font-weight: 600; color: #1a1033; }
.field__opt { font-weight: 400; color: #8b7d9e; font-size: 12px; margin-left: 4px; }
.field__box {
  display: flex; align-items: center; background: #fff; border: 2px solid #eee2ec;
  border-radius: 14px; padding: 0 14px; transition: border-color .2s, box-shadow .2s;
}
.field__box:focus-within { border-color: #ff4d8d; box-shadow: 0 0 0 4px rgba(255,77,141,.1); }
.field__ico { font-size: 18px; margin-right: 10px; }
.field__box input {
  flex: 1; border: none; outline: none; background: transparent;
  padding: 14px 0; font-size: 15px; color: #1a1033; font-family: inherit;
}
.field__box input::placeholder { color: #b8a9c9; }
.eye { background: none; border: none; cursor: pointer; font-size: 18px; padding: 4px 0 4px 8px; }

.phone-box { padding-left: 0; }
.dial {
  display: flex; align-items: center; gap: 6px; padding: 14px 14px;
  background: #faf3f8; border: none; border-right: 1px solid #eee2ec;
  border-radius: 12px 0 0 12px; cursor: pointer; font-family: inherit; font-size: 15px; color: #1a1033;
}
.flag { font-size: 20px; }
.code { font-weight: 700; }
.chev { font-size: 10px; opacity: .6; }
.hint { font-size: 13px; color: #8d7fa6; margin: -4px 0 4px; }

.auth-otp {
  display: grid;
  grid-template-columns: repeat(6, minmax(44px, 1fr));
  gap: 8px;
  margin-bottom: 4px;
  width: 100%;
}
.auth-otp__cell {
  width: 100%;
  height: 52px;
  min-width: 0;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: #fff;
  border: 2px solid #eee2ec;
  border-radius: 12px;
  outline: none;
  color: #1a1033;
  font-family: inherit;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  transition: border-color .2s, box-shadow .2s;
}
.auth-otp__cell:focus {
  border-color: #ff4d8d;
  box-shadow: 0 0 0 4px rgba(255,77,141,.12);
}
.resend { font-size: 14px; color: #8d7fa6; text-align: center; margin: 4px 0; }

.dev-banner {
  background: #fff3cd; border: 1px solid #ffe08a; color: #7a5900;
  padding: 10px 14px; border-radius: 12px; font-size: 13px; margin-bottom: 14px;
}

.check { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: #4a3d66; cursor: pointer; }
.check input { accent-color: #ff4d8d; width: 16px; height: 16px; margin-top: 2px; flex-shrink: 0; }
.check--terms { line-height: 1.5; }

.btn {
  font-family: inherit; font-weight: 700; cursor: pointer; border: none; outline: none;
  border-radius: 14px; padding: 16px; font-size: 15px; transition: transform .15s, box-shadow .2s;
  display: flex; align-items: center; justify-content: center;
}
.btn--primary {
  background: linear-gradient(90deg, #ff4d8d 0%, #a020f0 100%); color: #fff;
  box-shadow: 0 10px 24px rgba(255,77,141,.3);
}
.btn--primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(255,77,141,.45); }
.btn--primary:disabled { opacity: .5; cursor: not-allowed; }
.spinner {
  width: 18px; height: 18px; border: 2.5px solid rgba(255,255,255,.35); border-top-color: #fff;
  border-radius: 50%; animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.link { color: #ff4d8d; font-weight: 600; text-decoration: none; }
.link:hover { text-decoration: underline; }
.auth__foot { margin-top: 28px; text-align: center; font-size: 14px; color: #4a3d66; }
.auth__foot a { color: #ff4d8d; font-weight: 700; text-decoration: none; }
.auth__foot a:hover { text-decoration: underline; }

/* Picker */
.picker-mask {
  position: fixed; inset: 0; background: rgba(26,16,51,.5); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px;
}
.picker {
  width: 100%; max-width: 460px; max-height: 80vh; background: #fff; border-radius: 24px;
  display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 40px 80px rgba(0,0,0,.3);
}
.picker__head { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; border-bottom: 1px solid #eee2ec; }
.picker__head h3 { margin: 0; font-size: 18px; }
.picker__x { background: none; border: none; font-size: 20px; cursor: pointer; color: #6b5c85; }
.picker__search {
  margin: 16px 24px; padding: 12px 16px; border: 2px solid #eee2ec; border-radius: 12px;
  font-size: 15px; outline: none; font-family: inherit;
}
.picker__search:focus { border-color: #ff4d8d; }
.picker__list { flex: 1; overflow-y: auto; padding: 0 12px 12px; }
.picker__item {
  display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 12px; cursor: pointer;
  transition: background .15s;
}
.picker__item:hover { background: #fff9fb; }
.picker__item.active { background: linear-gradient(90deg, rgba(255,77,141,.1), rgba(160,32,240,.1)); }
.picker__flag { font-size: 22px; }
.picker__name { flex: 1; font-size: 15px; color: #1a1033; }
.picker__dial { color: #8d7fa6; font-weight: 600; font-size: 14px; }
.picker__empty { text-align: center; padding: 40px; color: #8d7fa6; }

/* ====== RESPONSIVE ====== */
@media (max-width: 960px) {
  .auth {
    grid-template-columns: 1fr;
    background:
      radial-gradient(1200px 500px at -20% -10%, rgba(255,77,141,.35), transparent 60%),
      radial-gradient(900px 500px at 120% -10%, rgba(160,32,240,.35), transparent 60%),
      linear-gradient(180deg, #fff9fb 0%, #fff 60%);
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }
  .auth::before, .auth::after {
    content: ''; position: absolute; border-radius: 50%;
    filter: blur(70px); opacity: .45; pointer-events: none; z-index: 0;
  }
  .auth::before {
    width: 360px; height: 360px; background: #ff4d8d;
    top: -120px; left: -120px; animation: authFloat 14s ease-in-out infinite;
  }
  .auth::after {
    width: 320px; height: 320px; background: #a020f0;
    top: 10%; right: -120px; animation: authFloat 16s ease-in-out infinite reverse;
  }
  @keyframes authFloat {
    0%, 100% { transform: translate(0,0) scale(1); }
    50% { transform: translate(30px,40px) scale(1.08); }
  }

  .auth__hero { display: none; }
  .auth__form-wrap {
    position: relative; z-index: 1;
    padding: 24px 20px 40px;
    align-items: flex-start;
  }
  .auth__form {
    background: rgba(255,255,255,.85);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(255,255,255,.7);
    border-radius: 28px;
    padding: 28px 24px 32px;
    box-shadow: 0 30px 60px rgba(160,32,240,.18), 0 8px 24px rgba(26,16,51,.06);
    max-width: 460px;
  }
  .brand--mobile { display: flex; }
  .brand--mobile .brand__name {
    background: linear-gradient(90deg, #ff4d8d, #a020f0);
    -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  }
  .backbtn {
    background: rgba(255,255,255,.7);
    box-shadow: 0 4px 12px rgba(26,16,51,.08);
  }
  .auth__title { font-size: 28px; }
  .auth__sub { font-size: 14px; }
}

@media (max-width: 520px) {
  .auth__form-wrap { padding: 20px 16px 32px; }
  .auth__form { padding: 24px 20px 28px; border-radius: 24px; }
  .auth__title { font-size: 24px; line-height: 1.2; }
  .auth__sub { font-size: 14px; margin-bottom: 22px; }
  .field__label { font-size: 12px; }
  .field__box { border-radius: 12px; }
  .field__box input { font-size: 16px; /* prevent iOS zoom */ padding: 13px 0; }
  .btn { padding: 15px; font-size: 15px; border-radius: 12px; }
  .brand--mobile .brand__name { font-size: 20px; }
  .brand--mobile .brand__icon { width: 32px; height: 32px; }
  .backbtn { width: 36px; height: 36px; font-size: 18px; }

  /* Step dots slimmer on small screens */
  .steps { gap: 6px; margin-bottom: 24px; }
  .dot { width: 28px; height: 28px; font-size: 12px; }

  /* Phone row */
  .dial { padding: 13px 10px; font-size: 14px; gap: 4px; }
  .flag { font-size: 18px; }
  .code { font-size: 14px; }

  .auth-otp { gap: 6px; grid-template-columns: repeat(6, minmax(40px, 1fr)); }
  .auth-otp__cell { height: 48px; font-size: 20px; }

  .hint { font-size: 12px; }
  .resend { font-size: 13px; }
  .check--terms { font-size: 12px; line-height: 1.5; }
  .auth__foot { font-size: 13px; margin-top: 24px; }
  .dev-banner { font-size: 12px; padding: 9px 12px; }

  /* Picker */
  .picker-mask { padding: 0; align-items: flex-end; }
  .picker {
    max-width: 100%; width: 100%;
    max-height: 85vh;
    border-radius: 24px 24px 0 0;
    animation: slideUp .25s ease-out;
  }
  .picker__head { padding: 16px 20px; }
  .picker__search { margin: 12px 20px; }
  .picker__item { padding: 12px; }
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
}

@media (max-width: 360px) {
  .auth__form { padding: 20px 16px 24px; }
  .auth__title { font-size: 21px; }
  .auth-otp__cell { height: 44px; font-size: 18px; }
  .dot { width: 24px; height: 24px; font-size: 11px; }
  .dial { padding: 12px 8px; }
  .code { font-size: 13px; }
}
</style>
